import { useRef, useState, useEffect, useImperativeHandle, forwardRef, useCallback } from "react";
import * as d3 from "d3";
import { fieldValue } from "../lib/visibility";
import { buildLocationClusters, UNPLACED_KEY } from "../lib/locationClusters";

const RADII = [24, 18, 14, 11];
const CONTEXT_COLORS = {
  professional: "#1C3F5E",
  personal: "#7A526A",
};

// Manual branches can push a node past degree 3 (e.g. branched off a 2nd-degree
// contact). Clamp every degree→array lookup so deep nodes render like degree 3.
const radiusFor = (degree) => RADII[Math.min(degree, RADII.length - 1)];

const REL_COLORS = {
  friend: "#2D6A4F",
  colleague: "#1C3F5E",
  alumni: "#92400E",
  family: "#6D28D9",
  acquaintance: "#4B5563",
  other: "#6B7280",
};

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || "?";
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const NetworkGraph = forwardRef(function NetworkGraph(
  { nodes, edges, matchedNodeIds, visibleNodeIds },
  ref
) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const linkRef = useRef(null);
  const nodeRef = useRef(null);
  const zoomRef = useRef(null);
  const simRef = useRef(null);
  const tooltipSetterRef = useRef(null);
  const fitRef = useRef(null);

  const [tooltip, setTooltip] = useState(null);
  tooltipSetterRef.current = setTooltip;

  // ── Imperative API ──────────────────────────────────────────────────────────
  useImperativeHandle(ref, () => ({
    highlightPath(pathIds) {
      if (!nodeRef.current || !linkRef.current) return;
      const pathSet = new Set(pathIds);
      nodeRef.current.style("opacity", (d) => (pathSet.has(d.id) ? 1 : 0.25));
      linkRef.current
        .style("opacity", (d) => {
          const onPath =
            pathSet.has(typeof d.source === "object" ? d.source.id : d.source) &&
            pathSet.has(typeof d.target === "object" ? d.target.id : d.target);
          return onPath ? 1 : 0.04;
        })
        .classed("path-highlighted", (d) => {
          const onPath =
            pathSet.has(typeof d.source === "object" ? d.source.id : d.source) &&
            pathSet.has(typeof d.target === "object" ? d.target.id : d.target);
          return onPath;
        });
    },
    resetHighlight() {
      if (!nodeRef.current || !linkRef.current) return;
      nodeRef.current.style("opacity", 1);
      linkRef.current
        .style("opacity", (d) => {
          const s = d.connectionStrength;
          if (s === "strong") return 0.85;
          if (s === "moderate") return 0.65;
          return 0.4;
        })
        .classed("path-highlighted", false);
    },
    zoomIn() {
      if (!zoomRef.current || !svgRef.current) return;
      d3.select(svgRef.current).transition().duration(300).call(zoomRef.current.scaleBy, 1.3);
    },
    zoomOut() {
      if (!zoomRef.current || !svgRef.current) return;
      d3.select(svgRef.current).transition().duration(300).call(zoomRef.current.scaleBy, 0.7);
    },
    resetView() {
      if (fitRef.current) return fitRef.current();
      if (!zoomRef.current || !svgRef.current) return;
      d3.select(svgRef.current)
        .transition()
        .duration(400)
        .call(zoomRef.current.transform, d3.zoomIdentity);
    },
  }));

  // ── Graph setup ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!nodes.length || !svgRef.current || !containerRef.current) return;

    const container = containerRef.current;
    const w = container.clientWidth || 800;
    const h = container.clientHeight || 600;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("width", w).attr("height", h);

    const g = svg.append("g").attr("class", "zoom-container");

    // Zoom
    const zoom = d3
      .zoom()
      .scaleExtent([0.1, 4])
      .on("zoom", (event) => g.attr("transform", event.transform));
    svg.call(zoom);
    zoomRef.current = zoom;

    // Clone data for D3
    const nodeData = nodes.map((n) => ({ ...n }));
    const nodeById = new Map(nodeData.map((n) => [n.id, n]));

    const edgeData = edges
      .map((e) => ({
        ...e,
        source: nodeById.get(e.source) || e.source,
        target: nodeById.get(e.target) || e.target,
      }))
      .filter((e) => typeof e.source === "object" && typeof e.target === "object");

    // Pin "You" node
    const youNode = nodeData.find((n) => n.degree === 0);
    if (youNode) {
      youNode.fx = w / 2;
      youNode.fy = h / 2;
    }

    // Layout depth = hops from "You" along the parentUid chain. Drives concentric
    // rings so a branch sits OUTSIDE the connection it hangs off, instead of every
    // node collapsing into a cluster around the center.
    const depthOf = (n) => {
      let depth = 0;
      let cur = n;
      const seen = new Set();
      while (cur && cur.parentUid && !seen.has(cur.id) && depth < 32) {
        seen.add(cur.id);
        const parent = nodeById.get(cur.parentUid);
        if (!parent) break;
        depth += 1;
        cur = parent;
      }
      return depth;
    };
    nodeData.forEach((n) => {
      n.layoutDepth = n.degree === 0 ? 0 : Math.max(1, depthOf(n));
    });

    // The dashboard graph lives in a narrow ledger column, while Search gets most
    // of the viewport. Scale ring spacing to the actual canvas so a two-hop
    // personal branch stays readable instead of shrinking every node.
    const ringStep = Math.min(150, Math.max(84, w * 0.28));
    const RING = [0, ringStep, ringStep * 1.75, ringStep * 2.4];
    const ringRadius = (depth) => RING[Math.min(depth, RING.length - 1)];

    // Location clustering: the ring says how far someone is from you, and the
    // ANGLE says where they live. People from the same place therefore share a
    // wedge, and the two readings never fight each other.
    const grouping = buildLocationClusters(nodeData);
    const clusterSeats = grouping.isMeaningful ? grouping.byNodeId : new Map();
    const clustered = clusterSeats.size > 0;

    nodeData.forEach((n) => {
      const seat = clusterSeats.get(n.id);
      n.cluster = seat?.cluster || null;
      n.seatAngle = seat ? seat.angle : null;
      if (!seat) return;
      // Seed each node inside its wedge so the simulation settles into the
      // grouping instead of untangling into it from a random start.
      const r = ringRadius(n.layoutDepth) || ringStep;
      n.x = w / 2 + Math.cos(seat.angle) * r;
      n.y = h / 2 + Math.sin(seat.angle) * r;
    });

    const seatX = (d) => w / 2 + Math.cos(d.seatAngle) * ringRadius(d.layoutDepth);
    const seatY = (d) => h / 2 + Math.sin(d.seatAngle) * ringRadius(d.layoutDepth);

    // Simulation — radial rings by depth spread the graph outward; links keep each
    // node angularly near its parent so branches read as extending off them. No
    // center gravity: the radial force + the pinned "You" keep it centered.
    const sim = d3
      .forceSimulation(nodeData)
      .force(
        "link",
        d3
          .forceLink(edgeData)
          .id((d) => d.id)
          // Links pull harder than the seats would like, so ease off once the
          // map is grouped by place.
          .distance(110)
          .strength(clustered ? 0.04 : 0.25)
      )
      .force(
        "charge",
        // When the map is grouped by place, the seats decide where people go;
        // repulsion only needs to stop them overlapping, not to spread them.
        d3.forceManyBody().strength((d) => {
          if (d.degree === 0) return -700;
          return clustered ? -120 : -280;
        })
      )
      .force(
        "radial",
        d3
          .forceRadial((d) => ringRadius(d.layoutDepth), w / 2, h / 2)
          .strength((d) => {
            if (d.layoutDepth === 0) return 0;
            // A seated node already has its radius from the seat force.
            return clustered && d.seatAngle !== null ? 0.15 : 0.55;
          })
      )
      .force(
        "collide",
        d3.forceCollide().radius((d) => radiusFor(d.degree) + 18)
      )
      .alphaMin(0.001);

    if (clustered) {
      sim
        .force("clusterX", d3.forceX(seatX).strength((d) => (d.seatAngle === null ? 0 : 0.9)))
        .force("clusterY", d3.forceY(seatY).strength((d) => (d.seatAngle === null ? 0 : 0.9)));
    }

    simRef.current = sim;

    // Location sectors sit behind everything. They are drawn once the
    // simulation settles, so the wedges hug where people actually ended up
    // rather than a guessed radius.
    const sectorsG = clustered ? g.append("g").attr("class", "clusters-layer") : null;
    let sectorPoints = [];

    const renderSectors = () => {
      if (!sectorsG) return;
      sectorsG.selectAll("*").remove();
      sectorPoints = [];

      const spread = Math.max(
        ...nodeData.map((n) => Math.hypot(n.x - w / 2, n.y - h / 2)),
        ringStep
      );
      const outer = spread + 30;
      // d3.arc measures from 12 o'clock; our seat angles start at 3 o'clock.
      const toArc = (angle) => angle + Math.PI / 2;

      grouping.clusters.forEach((cluster, index) => {
        const isUnplaced = cluster.key === UNPLACED_KEY || !cluster.label;

        if (!isUnplaced) {
          sectorsG
            .append("path")
            .attr("class", `cluster-wedge${index % 2 ? " is-alt" : ""}`)
            .attr("transform", `translate(${w / 2},${h / 2})`)
            .attr(
              "d",
              d3
                .arc()
                .innerRadius(0)
                .outerRadius(outer)
                .startAngle(toArc(cluster.startAngle))
                .endAngle(toArc(cluster.endAngle))()
            );
        }

        sectorsG
          .append("line")
          .attr("class", "cluster-divider")
          .attr("x1", w / 2)
          .attr("y1", h / 2)
          .attr("x2", w / 2 + Math.cos(cluster.startAngle) * outer)
          .attr("y2", h / 2 + Math.sin(cluster.startAngle) * outer);

        if (isUnplaced) return;

        const lx = w / 2 + Math.cos(cluster.angle) * (outer - 4);
        const ly = h / 2 + Math.sin(cluster.angle) * (outer - 4);
        const onLeft = Math.cos(cluster.angle) < -0.2;
        const onRight = Math.cos(cluster.angle) > 0.2;
        const text = `${cluster.label} · ${cluster.nodeIds.length}`;

        sectorsG
          .append("text")
          .attr("class", "cluster-label")
          .attr("x", lx)
          .attr("y", ly)
          .attr("text-anchor", onLeft ? "end" : onRight ? "start" : "middle")
          .attr("dominant-baseline", "middle")
          .text(text);

        // Reserve room for the label so fitToView does not crop it.
        const width = text.length * 6;
        sectorPoints.push({ x: lx + (onLeft ? -width : onRight ? width : width / 2), y: ly });
        sectorPoints.push({ x: lx, y: ly + 10 });
        sectorPoints.push({ x: lx, y: ly - 10 });
      });
    };

    // Nodes are seeded into their wedges, so the sectors can be drawn straight
    // away rather than popping in several seconds later when the force
    // simulation settles. They are redrawn on settle to hug the final layout.
    renderSectors();

    // Edges
    const link = g
      .append("g")
      .attr("class", "edges-layer")
      .selectAll("line")
      .data(edgeData)
      .join("line")
      .attr("class", "edge-line")
      .style("stroke", (d) => {
        if (d.context === "professional") return CONTEXT_COLORS.professional;
        if (d.context === "personal") return CONTEXT_COLORS.personal;
        if (d.context === "all") return "#5F5A54";
        if (d.secondary) return "#8B93C4"; // faint "via" provenance link
        const s = d.connectionStrength;
        if (s === "strong") return "var(--color-accent)";
        if (s === "moderate") return "#6B8CA9";
        return "#c0bab4";
      })
      .style("stroke-width", (d) => {
        if (d.secondary) return 1.5;
        const s = d.connectionStrength;
        if (s === "strong") return 3;
        if (s === "moderate") return 2;
        return 1.5;
      })
      .style("stroke-dasharray", (d) => {
        if (d.boundary) return "4 5";
        if (d.secondary) return "2 4";
        return d.connectionStrength === "weak" ? "5 3" : null;
      })
      .style("opacity", (d) => {
        if (d.secondary) return 0.5;
        const s = d.connectionStrength;
        if (s === "strong") return 0.85;
        if (s === "moderate") return 0.65;
        return 0.4;
      });
    linkRef.current = link;

    // Nodes
    const node = g
      .append("g")
      .attr("class", "nodes-layer")
      .selectAll("g")
      .data(nodeData)
      .join("g")
      .attr("class", "node-group")
      .style("cursor", (d) => (d.degree <= 2 ? "pointer" : "default"));

    // Match glow circle
    node
      .append("circle")
      .attr("class", "match-glow")
      .attr("r", (d) => radiusFor(d.degree) + 6)
      .attr("fill", "var(--color-accent-light)")
      .attr("opacity", 0);

    // Main circle
    // Context is drawn around the person, rather than replacing relationship
    // color. Dual-context nodes get two half-rings: navy + plum.
    const contextNodes = node.filter((d) => d.degree > 0 && (d.contexts?.length || d.context));
    ["professional", "personal"].forEach((context, index) => {
      contextNodes
        .append("circle")
        .attr("class", `context-ring context-ring-${context}`)
        .attr("r", (d) => radiusFor(d.degree) + 4)
        .attr("fill", "none")
        .attr("stroke", CONTEXT_COLORS[context])
        .attr("stroke-width", (d) => (d.boundary ? 1.5 : 2.5))
        .attr("stroke-dasharray", (d) => {
          const contexts = d.contexts || (d.context ? [d.context] : []);
          if (!contexts.includes(context)) return "0 999";
          if (contexts.length < 2) return null;
          const circumference = 2 * Math.PI * (radiusFor(d.degree) + 4);
          return `${circumference / 2} ${circumference / 2}`;
        })
        .attr("stroke-dashoffset", (d) => {
          const contexts = d.contexts || (d.context ? [d.context] : []);
          if (contexts.length < 2 || index === 0) return 0;
          return -Math.PI * (radiusFor(d.degree) + 4);
        })
        .attr("opacity", (d) => (d.boundary ? 0.55 : 1));
    });

    node
      .append("circle")
      .attr("class", "node-circle")
      .attr("r", (d) => radiusFor(d.degree))
      .attr("fill", (d) => {
        if (d.degree === 0) return "var(--color-accent)";
        if (d.boundary) return "#F8F7F4";
        // "Met them through X" — your contact, shown as a filled colored circle.
        if (d.directBranch) return REL_COLORS[d.relationshipType] || REL_COLORS.other;
        if (d.degree === 1) return "var(--color-surface)";
        if (d.degree === 2) return "var(--color-surface)";
        return "#F0EFEC";
      })
      .attr("stroke", (d) => {
        if (d.degree === 0) return "var(--color-accent)";
        if (d.boundary) return "#AAA29B";
        if (d.directBranch) return REL_COLORS[d.relationshipType] || REL_COLORS.other;
        if (d.degree === 1) return REL_COLORS[d.relationshipType] || REL_COLORS.other;
        return "#c0bab4";
      })
      .attr("stroke-width", (d) => (d.degree <= 1 ? 2.5 : 1.5))
      .attr("stroke-dasharray", (d) => (d.degree >= 2 && !d.directBranch ? "3 2" : null));

    // Text inside nodes
    node
      .filter((d) => d.degree <= 1)
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "central")
      .attr("font-size", (d) => (d.degree === 0 ? "10px" : "9px"))
      .attr("font-weight", "600")
      .attr("fill", (d) => (d.degree === 0 || d.directBranch ? "#fff" : "var(--color-text-secondary)"))
      .text((d) =>
        d.degree === 0 ? "You" : getInitials(d.profile?.full_name)
      );

    node
      .filter((d) => d.degree >= 2)
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "central")
      .attr("font-size", "9px")
      .attr("font-weight", "600")
      .attr("fill", "var(--color-text-tertiary)")
      .text((d) => getInitials(d.profile?.full_name));

    // Label below (degree 0, 1, and 2)
    node
      .filter((d) => d.degree <= 2)
      .append("text")
      .attr("class", "node-label")
      .attr("text-anchor", "middle")
      .attr("dy", (d) => RADII[Math.min(d.degree, RADII.length - 1)] + 12)
      .attr("font-size", "10px")
      .attr("fill", "var(--color-text-secondary)")
      .text((d) => {
        if (d.degree === 0) return "";
        if (d.degree === 2) {
          const first = d.profile?.full_name?.split(" ")[0] || "";
          return first.length > 12 ? first.slice(0, 11) + "…" : first;
        }
        const name = d.profile?.full_name || "";
        return name.length > 14 ? name.slice(0, 13) + "…" : name;
      });

    // Drag
    node.call(
      d3
        .drag()
        .on("start", (event, d) => {
          if (!event.active) sim.alphaTarget(0.3).restart();
          if (d.degree !== 0) { d.fx = d.x; d.fy = d.y; }
        })
        .on("drag", (event, d) => {
          if (d.degree !== 0) { d.fx = event.x; d.fy = event.y; }
        })
        .on("end", (event, d) => {
          if (!event.active) sim.alphaTarget(0);
          if (d.degree !== 0) { d.fx = null; d.fy = null; }
        })
    );

    // Tooltip
    node
      .on("mouseenter", (event, d) => {
        const rect = container.getBoundingClientRect();
        tooltipSetterRef.current({
          node: d,
          x: event.clientX - rect.left + 12,
          y: event.clientY - rect.top - 8,
        });
      })
      .on("mouseleave", () => tooltipSetterRef.current(null));

    nodeRef.current = node;

    // Tick
    sim.on("tick", () => {
      link
        .attr("x1", (d) => d.source.x)
        .attr("y1", (d) => d.source.y)
        .attr("x2", (d) => d.target.x)
        .attr("y2", (d) => d.target.y);
      node.attr("transform", (d) => `translate(${d.x},${d.y})`);
    });

    // Fit the whole graph into view once it settles — the radial spread can
    // exceed the panel, especially the narrow dashboard column.
    const fitToView = () => {
      if (!nodeData.length || !svgRef.current) return;
      const pad = 34;
      const points = [...nodeData, ...sectorPoints];
      const xs = points.map((n) => n.x);
      const ys = points.map((n) => n.y);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      const gw = Math.max(maxX - minX, 1);
      const gh = Math.max(maxY - minY, 1);
      const scale = Math.min((w - pad * 2) / gw, (h - pad * 2) / gh, 1.4);
      const tx = w / 2 - (scale * (minX + maxX)) / 2;
      const ty = h / 2 - (scale * (minY + maxY)) / 2;
      d3.select(svgRef.current)
        .transition()
        .duration(400)
        .call(zoom.transform, d3.zoomIdentity.translate(tx, ty).scale(scale));
    };
    fitRef.current = fitToView;

    let fitted = false;
    sim.on("end", () => {
      if (fitted) return;
      fitted = true;
      renderSectors();
      fitToView();
    });

    return () => {
      sim.stop();
      svg.on(".zoom", null);
    };
  }, [nodes, edges]);

  // ── Match glow update ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!nodeRef.current) return;
    nodeRef.current
      .select(".match-glow")
      .attr("opacity", (d) => (matchedNodeIds?.has(d.id) ? 0.6 : 0));
  }, [matchedNodeIds]);

  // ── Visibility filter ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!nodeRef.current || !linkRef.current) return;
    if (!visibleNodeIds) {
      nodeRef.current.style("display", null);
      linkRef.current.style("display", null);
    } else {
      nodeRef.current.style("display", (d) =>
        visibleNodeIds.has(d.id) ? null : "none"
      );
      linkRef.current.style("display", (d) => {
        const s = typeof d.source === "object" ? d.source.id : d.source;
        const t = typeof d.target === "object" ? d.target.id : d.target;
        return visibleNodeIds.has(s) && visibleNodeIds.has(t) ? null : "none";
      });
    }
  }, [visibleNodeIds]);

  // ── Tooltip content ─────────────────────────────────────────────────────────
  const renderTooltipContent = useCallback((node) => {
    if (!node) return null;
    const p = node.profile || {};

    if (node.degree === 0) {
      return <strong>{p.full_name || "You"}</strong>;
    }
    if (node.degree === 1) {
      const role = fieldValue(p, "role");
      const school = fieldValue(p, "school");
      return (
        <>
          <div className="graph-tooltip-name">{p.full_name || "Unknown"}</div>
          {node.boundary && <div className="graph-tooltip-context">Private bridge</div>}
          {!node.boundary && node.contexts?.length > 0 && (
            <div className="graph-tooltip-context">
              {node.contexts.map((context) => context[0].toUpperCase() + context.slice(1)).join(" · ")}
            </div>
          )}
          {role && <div className="graph-tooltip-sub">{role}</div>}
          {school && <div className="graph-tooltip-sub">{school}</div>}
        </>
      );
    }
    if (node.degree === 2) {
      const first = p.full_name?.split(" ")[0] || "Unknown";
      const role = fieldValue(p, "role");
      const industry = fieldValue(p, "industry");
      return (
        <>
          <div className="graph-tooltip-name">{first}</div>
          {role && <div className="graph-tooltip-sub">{role}</div>}
          {industry && <div className="graph-tooltip-sub">{industry}</div>}
        </>
      );
    }
    return null;
  }, []);

  return (
    <div ref={containerRef} className="graph-container">
      <svg ref={svgRef} />
      {tooltip && (
        <div
          className="graph-tooltip"
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          {renderTooltipContent(tooltip.node)}
        </div>
      )}
    </div>
  );
});

export default NetworkGraph;
